// Bodies (04 §Base rules: BlockNote JSON for `body`; the API also accepts `body_markdown` on write,
// converted server-side, and returns `body_text`, so agents never need to emit BlockNote JSON).
// The converter covers the block structure agents write — headings, paragraphs, bullet and
// numbered lists, check lists, quotes, code blocks — with inline text kept as plain text.

export type Block = Record<string, unknown>;

const text = (s: string) => (s === "" ? [] : [{ type: "text", text: s, styles: {} }]);
const block = (type: string, content: string, props: Record<string, unknown> = {}): Block => ({
  type,
  props,
  content: text(content),
  children: [],
});

// KB-PDF-UPLOAD: pipe tables (`| a | b |` rows, an optional `| --- |` divider after the header)
// are BlockNote `table` blocks, so an uploaded document's tables stay tables.
const TABLE_ROW = /^\s*\|.*\|\s*$/;
const TABLE_DIVIDER = /^\s*\|(\s*:?-{3,}:?\s*\|)+\s*$/;

function splitRow(line: string): string[] {
  const inner = line.trim().replace(/^\|/, "").replace(/\|$/, "");
  return inner.split(/(?<!\\)\|/).map((c) => c.trim().replace(/\\\|/g, "|"));
}

const tableCell = (s: string) => ({
  type: "tableCell",
  props: {
    backgroundColor: "default",
    textColor: "default",
    textAlignment: "left",
    colspan: 1,
    rowspan: 1,
  },
  content: text(s),
});

function tableBlock(rows: string[][], header: boolean): Block {
  const width = Math.max(...rows.map((r) => r.length));
  return {
    type: "table",
    props: { textColor: "default" },
    content: {
      type: "tableContent",
      columnWidths: Array(width).fill(null),
      ...(header ? { headerRows: 1 } : {}),
      rows: rows.map((r) => ({
        cells: [...r, ...Array(width - r.length).fill("")].map((c) => tableCell(c)),
      })),
    },
    children: [],
  };
}

/** A `table` block's rows as cell texts (cells either `tableCell`s or bare inline arrays). */
export function tableRowsOf(content: unknown): string[][] | null {
  if (!content || typeof content !== "object" || Array.isArray(content)) return null;
  const rows = (content as { rows?: unknown }).rows;
  if (!Array.isArray(rows)) return null;
  return rows.map((r) => {
    const cells = (r as { cells?: unknown } | null)?.cells;
    if (!Array.isArray(cells)) return [];
    return cells.map((c) =>
      c && typeof c === "object" && !Array.isArray(c) && "content" in c
        ? inlineText((c as { content?: unknown }).content)
        : inlineText(c),
    );
  });
}

/** Rows as a pipe table (first row the header). */
export function rowsToPipeTable(rows: readonly (readonly string[])[]): string {
  const width = Math.max(1, ...rows.map((r) => r.length));
  const esc = (s: string) => s.replace(/\s+/g, " ").trim().replace(/\|/g, "\\|");
  const line = (r: readonly string[]) =>
    `| ${[...r, ...Array(width - r.length).fill("")].map(esc).join(" | ")} |`;
  const [head, ...body] = rows;
  return [line(head ?? []), `|${" --- |".repeat(width)}`, ...body.map(line)].join("\n");
}

/** Markdown → BlockNote blocks (one block per line or fenced code block). */
export function markdownToBlocks(md: string): Block[] {
  const lines = md.replace(/\r\n?/g, "\n").split("\n");
  const out: Block[] = [];
  let para: string[] = [];
  const flush = () => {
    if (para.length > 0) out.push(block("paragraph", para.join(" ")));
    para = [];
  };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] as string;
    if (TABLE_ROW.test(line) && !TABLE_DIVIDER.test(line)) {
      const rows: string[][] = [];
      let header = false;
      let j = i;
      while (j < lines.length && TABLE_ROW.test(lines[j] as string)) {
        if (TABLE_DIVIDER.test(lines[j] as string)) header ||= rows.length === 1;
        else rows.push(splitRow(lines[j] as string));
        j++;
      }
      flush();
      out.push(tableBlock(rows, header));
      i = j - 1;
      continue;
    }
    const fence = /^```\s*([\w+-]*)\s*$/.exec(line);
    if (fence) {
      flush();
      const code: string[] = [];
      i++;
      while (i < lines.length && !/^```\s*$/.test(lines[i] as string))
        code.push(lines[i++] as string);
      out.push(block("codeBlock", code.join("\n"), fence[1] ? { language: fence[1] } : {}));
      continue;
    }
    if (line.trim() === "") {
      flush();
      continue;
    }
    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    const check = /^\s*[-*+]\s+\[([ xX])\]\s+(.*)$/.exec(line);
    const bullet = /^\s*[-*+]\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    const quote = /^>\s?(.*)$/.exec(line);
    if (heading) {
      flush();
      out.push(
        block("heading", (heading[2] as string).trim(), { level: (heading[1] as string).length }),
      );
    } else if (check) {
      flush();
      out.push(block("checkListItem", (check[2] as string).trim(), { checked: check[1] !== " " }));
    } else if (bullet) {
      flush();
      out.push(block("bulletListItem", (bullet[1] as string).trim()));
    } else if (numbered) {
      flush();
      out.push(block("numberedListItem", (numbered[1] as string).trim()));
    } else if (quote) {
      flush();
      out.push(block("quote", (quote[1] as string).trim()));
    } else para.push(line.trim());
  }
  flush();
  return out;
}

/**
 * WIKI-LINKS-GRAPH: a doc link from the [[ picker (`pageLink`) is its title in text and the
 * `[[Title|uuid]]` it stands for in markdown (`wiki`), so a doc read and written back as markdown
 * keeps its links.
 */
function inlineText(content: unknown, wiki = false): string {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return "";
  return content
    .map((c) => {
      if (!c || typeof c !== "object") return "";
      const o = c as { type?: unknown; text?: unknown; content?: unknown; props?: unknown };
      if (typeof o.text === "string") return o.text;
      if (o.type === "pageLink" && o.props && typeof o.props === "object") {
        const p = o.props as { pageId?: unknown; title?: unknown };
        const title = typeof p.title === "string" ? p.title : "";
        if (!wiki) return title;
        const t = title.replace(/[[\]|]/g, " ").trim() || "Untitled";
        // no id: the `[[Title]]` it was written as
        return typeof p.pageId === "string" && p.pageId ? `[[${t}|${p.pageId}]]` : `[[${t}]]`;
      }
      return inlineText(o.content, wiki);
    })
    .join("");
}

/**
 * T6-BOARD-NOTE: BlockNote blocks → the markdown `markdownToBlocks` reads (headings, paragraphs,
 * lists, check lists, quotes, code), so a body written as markdown reads back the same. Children
 * are flattened after their block; inline styles are plain text.
 */
export function blocksToMarkdown(blocks: readonly Block[]): string {
  const out: string[] = [];
  const walk = (list: readonly unknown[]) => {
    for (const b of list) {
      if (!b || typeof b !== "object") continue;
      const o = b as { type?: unknown; props?: unknown; content?: unknown; children?: unknown };
      const props = (o.props ?? {}) as { level?: unknown; checked?: unknown; language?: unknown };
      const t = inlineText(o.content, true);
      switch (o.type) {
        case "heading": {
          const level = Math.min(3, Math.max(1, Number(props.level) || 1));
          out.push(`${"#".repeat(level)} ${t}`, "");
          break;
        }
        case "bulletListItem":
          out.push(`- ${t}`);
          break;
        case "numberedListItem":
          out.push(`1. ${t}`);
          break;
        case "checkListItem":
          out.push(`- [${props.checked ? "x" : " "}] ${t}`);
          break;
        case "quote":
          out.push(`> ${t}`, "");
          break;
        case "codeBlock":
          out.push(
            `\`\`\`${typeof props.language === "string" ? props.language : ""}`,
            t,
            "```",
            "",
          );
          break;
        case "table": {
          const rows = tableRowsOf(o.content);
          if (rows?.length) out.push(...rowsToPipeTable(rows).split("\n"), "");
          break;
        }
        default:
          if (t !== "") out.push(t, "");
      }
      if (Array.isArray(o.children)) walk(o.children);
    }
  };
  walk(blocks);
  // a list ends where the next paragraph or heading begins: one blank line between blocks
  const md: string[] = [];
  for (const [i, line] of out.entries()) {
    const prev = out[i - 1];
    const listy = (s: string | undefined) => s !== undefined && /^(- |1\. )/.test(s);
    if (line !== "" && listy(prev) && !listy(line)) md.push("");
    if (line === "" && md.at(-1) === "") continue;
    md.push(line);
  }
  return md.join("\n").trim();
}

/** The plain text of a BlockNote document (`body_text`): one line per block, children nested. */
export function blocksToText(blocks: readonly Block[]): string {
  const lines: string[] = [];
  const walk = (list: readonly unknown[]) => {
    for (const b of list) {
      if (!b || typeof b !== "object") continue;
      const o = b as { type?: unknown; content?: unknown; children?: unknown };
      if (o.type === "table") {
        // a table's rows, one line each, cells tab-separated (searchable, readable)
        for (const r of tableRowsOf(o.content) ?? [])
          if (r.some((c) => c)) lines.push(r.join("\t"));
      } else {
        const t = inlineText(o.content);
        if (t !== "") lines.push(t);
      }
      if (Array.isArray(o.children)) walk(o.children);
    }
  };
  walk(blocks);
  return lines.join("\n");
}
